import React from 'react';
import ReviewIcon from 'assets/images/riviewdicon.svg';
import { formatCurrency } from 'utils';
import type { AuditSummaryContext, TileConfig } from '../types';
import { buildMissingTicketsSubtext, resolveScratcherBadge } from './shared';

export function buildScratcherSalesTile(ctx: AuditSummaryContext): TileConfig {
  const source = ctx.isAllShift ? ctx.dailyTotals : ctx.auditTotals;
  const scratcherRevenueInAudit = source?.audit?.total_revenue ?? 0;
  const scratcherSoldQtyInAudit = source?.audit?.total_sold ?? 0;
  const scratcherRevenueInPOS = source?.pos?.scratcher_sales ?? 0;
  const scratcherSoldQtyInPOS = source?.pos?.scratcher_count ?? 0;
  const scratcherRevenueVariance = source?.reconciliation_variance?.scratcher?.revenue_variance ?? 0;
  const scratcherQuantityVariance = source?.reconciliation_variance?.scratcher?.quantity_variance ?? 0;

  const isLottoPosMode = ctx.mode === 'posOnly' || ctx.mode === 'bothConnected';

  const cardClassName =
    ctx.mode === 'posOnly'
      ? 'manual-entry-card manual-entry-centered-card'
      : ctx.mode === 'lottoOnly'
        ? 'compact-tall-summary-card'
        : ctx.mode === 'bothNotConnected'
          ? 'compact-tall-manual-card'
          : ctx.mode === 'bothConnected' && ctx.isPosValueMissing && ctx.isAllShift
            ? 'pos-missing-input-card'
          : undefined;

  const containerSx = { marginRight: ctx.isAllShift && ctx.mode !== 'bothConnected' ? '124px' : 0 };

  if (isLottoPosMode) {
    const hasScratcherSalesInput = ctx.isPosValueMissing && ctx.posMissingScratcherSalesInput !== '';
    const hasScratcherQtyInput = ctx.isPosValueMissing && ctx.posMissingScratcherQtyInput !== '';

    const effectivePosSales = hasScratcherSalesInput
      ? Number(ctx.posMissingScratcherSalesInput) || 0
      : scratcherRevenueInPOS;
    const effectivePosQty = hasScratcherQtyInput
      ? Number(ctx.posMissingScratcherQtyInput) || 0
      : Number(scratcherSoldQtyInPOS);

    const effectiveRevenueVariance = effectivePosSales - Number(scratcherRevenueInAudit);
    const effectiveQuantityVariance = effectivePosQty - Number(scratcherSoldQtyInAudit);

    const badge = resolveScratcherBadge({
      revenueVariance: effectiveRevenueVariance,
      quantityVariance: effectiveQuantityVariance,
    });
    const hasNoVariance = effectiveRevenueVariance === 0 && effectiveQuantityVariance === 0;

    return {
      id: 'scratcher_sales',
      visible: true,
      variant: 'standard_lotto_pos',
      title: 'SCRATCHER SALES',
      badge,
      value: hasNoVariance ? (
        <img
          src={ReviewIcon}
          alt="Review"
          className="review-icon"
          style={{ width: '32px', height: '32px' }}
        />
      ) : (
        formatCurrency(effectiveRevenueVariance)
      ),
      subtext: buildMissingTicketsSubtext({ badge, quantityVariance: effectiveQuantityVariance }),
      footer: `Total: ${formatCurrency(effectivePosSales)} (${effectivePosQty})`,
      actionLabel: 'Acknowledged',
      hideAction: ctx.mode === 'posOnly',
      cardClassName,
      inputPlaceholder: ctx.mode === 'bothConnected' && ctx.isPosValueMissing ? 'Enter POS Sales' : undefined,
      containerSx,
    };
  }

  let actionLabel = 'Mark Verified';
  if (ctx.mode === 'lottoOnly' || ctx.mode === 'bothNotConnected') {
    actionLabel = 'Verified in POS';
  }

  return {
    id: 'scratcher_sales',
    visible: true,
    variant: 'standard_summary',
    title: 'SCRATCHER SALES',
    value: formatCurrency(scratcherRevenueInAudit),
    subtext: `${scratcherSoldQtyInAudit} ${Number(scratcherSoldQtyInAudit) === 1 ? 'ticket' : 'tickets'}`,
    footer: 'Reported by Audit',
    actionLabel,
    cardClassName,
    containerSx,
  };
}

